import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Network, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NodeDialog, EdgeDialog, FieldsDialog } from "@/components/curator/CuratorDialogs";
import { CuratorSidebar } from "@/components/curator/CuratorSidebar";
import { NodeEditor } from "@/components/curator/NodeEditor";
import { RelationshipsPanel } from "@/components/curator/RelationshipsPanel";
import { ImportPanel } from "@/components/curator/ImportPanel";
import { ReviewPanel } from "@/components/curator/ReviewPanel";
import { ContributionsPanel } from "@/components/curator/ContributionsPanel";
import { DuplicatesPanel } from "@/components/curator/DuplicatesPanel";
import { MergeDialog } from "@/components/curator/MergeDialog";
import { ResearchPanel } from "@/components/ai/ResearchPanel";

const NODE_PAGE = 25;

const KGCuratorView = () => {
  const { user } = useAuth();
  const [schema, setSchema] = useState(null);
  const [stats, setStats] = useState({});
  const [active, setActive] = useState("knowledge_system");
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [nodeDialog, setNodeDialog] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [edgeDialog, setEdgeDialog] = useState(false);
  const [editingEdge, setEditingEdge] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {kind:'node'|'edge', id}
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [contributions, setContributions] = useState([]);
  const [contributionCount, setContributionCount] = useState(0);
  const [nodeQuery, setNodeQuery] = useState("");
  const [nodeSkip, setNodeSkip] = useState(0);
  const [nodeTotal, setNodeTotal] = useState(0);
  const [fieldsDialog, setFieldsDialog] = useState(false);
  const [proposalDialog, setProposalDialog] = useState(null); // { nodeType, prefill }
  const [researchOpen, setResearchOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [merges, setMerges] = useState([]);
  const [mergeState, setMergeState] = useState(null); // { type, members }

  const loadDuplicates = useCallback(async () => {
    try {
      const [dups, mgs] = await Promise.all([api.get("/kg/duplicates"), api.get("/kg/merges")]);
      setDuplicates(dups.data);
      setMerges(mgs.data);
    } catch (e) { console.error("Failed to load duplicates/merges:", e); }
  }, []);

  const loadReviewCount = useCallback(async () => {
    try { setReviewCount((await api.get("/kg/review-queue")).data.length); }
    catch (e) { console.error("Failed to load review count:", e); }
  }, []);
  const loadContributions = useCallback(async () => {
    const items = (await api.get("/curator/contributions")).data;
    setContributions(items);
    setContributionCount(items.length);
  }, []);
  const loadStats = useCallback(async () => {
    setStats((await api.get("/kg/stats")).data);
    loadReviewCount();
    loadContributions().catch(() => {});
    loadDuplicates();
  }, [loadReviewCount, loadContributions, loadDuplicates]);
  const loadSchema = useCallback(async () => setSchema((await api.get("/kg/schema")).data), []);
  useEffect(() => {
    loadSchema();
    loadStats();
  }, [loadSchema, loadStats]);

  const loadNodes = useCallback(async (type, q = "", skip = 0) => {
    const res = await api.get("/kg/nodes", {
      params: { type, q: q || undefined, skip, limit: NODE_PAGE },
    });
    setNodes(res.data);
    setNodeTotal(Number(res.headers["x-total-count"] ?? res.data.length));
  }, []);
  const loadEdges = useCallback(async () => setEdges((await api.get("/kg/edges")).data), []);
  const loadReview = useCallback(async () => setReviewItems((await api.get("/kg/review-queue")).data), []);

  // Reset pagination/search + selection when switching entity type.
  useEffect(() => { setNodeQuery(""); setNodeSkip(0); setSelected(new Set()); }, [active]);
  useEffect(() => { setSelected(new Set()); }, [nodes]);

  useEffect(() => {
    if (!schema) return;
    if (active === "relationships") loadEdges();
    else if (active === "review") { loadReview(); loadDuplicates(); }
    else if (active === "duplicates") loadDuplicates();
    else if (active === "contributions") loadContributions();
    else if (active !== "import") {
      const t = setTimeout(() => loadNodes(active, nodeQuery, nodeSkip), nodeQuery ? 300 : 0);
      return () => clearTimeout(t);
    }
  }, [active, schema, nodeQuery, nodeSkip, loadNodes, loadEdges, loadReview, loadContributions, loadDuplicates]);

  const refresh = () => {
    loadStats();
    if (active === "relationships") loadEdges();
    else if (active === "review") loadReview();
    else loadNodes(active, nodeQuery, nodeSkip);
  };

  const nodeAction = async (id, action, body) => {
    try {
      await api.post(`/kg/nodes/${id}/${action}`, body || {});
      toast.success({ submit: "Submitted for review", approve: "Approved & published",
        reject: "Change rejected", archive: "Archived", restore: "Restored to draft" }[action]);
      if (active === "review") loadReview(); else loadNodes(active, nodeQuery, nodeSkip);
      loadReviewCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const openMerge = (type, members) => {
    if (!members || members.length < 2) { toast.error("Pick at least two entities to merge"); return; }
    setMergeState({ type, members });
  };

  const resolveMerge = async (id, action) => {
    try {
      await api.post(`/kg/merges/${id}/resolve`, { action });
      toast.success(action === "approve" ? "Merge approved & applied" : "Merge rejected");
      loadDuplicates();
      loadReviewCount();
      if (active === "review") loadReview();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected((prev) =>
    prev.size === nodes.length ? new Set() : new Set(nodes.map((n) => n.id)));

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const res = await api.post("/kg/nodes/bulk", { action, node_ids: ids });
      const { success_count, error_count } = res.data;
      if (success_count) toast.success(`${success_count} ${success_count === 1 ? "entity" : "entities"} updated`);
      if (error_count) toast.error(`${error_count} skipped (see rules)`);
      setSelected(new Set());
      setBulkConfirm(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
      setBulkConfirm(false);
    }
  };

  const resolveContribution = async (id, action) => {
    try {
      await api.post(`/curator/contributions/${id}/resolve`, { action });
      toast.success(action === "accept" ? "Accepted — remember to update the entity" : "Dismissed");
      loadContributions();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const createEntityFromProposal = (c) => {
    const nt = schema?.node_types?.[c.proposed_type];
    if (!nt) { toast.error("Unknown entity type in this proposal"); return; }
    setProposalDialog({ nodeType: { key: c.proposed_type, ...nt }, prefill: { name: c.proposed_name }, contributionId: c.id });
  };

  const createEntityFromResearch = (text) => {
    const validType = schema?.node_types?.[active] ? active : "concept";
    const nt = schema.node_types[validType];
    const descField = nt.fields.find((f) => ["description", "content", "summary", "definition", "overview"].includes(f.key));
    const prefill = descField ? { fields: { [descField.key]: text } } : {};
    setProposalDialog({ nodeType: { key: validType, ...nt }, prefill, contributionId: null });
    setResearchOpen(false);
    toast.message(`Drafting a new ${nt.label || validType} from your research`);
  };

  const confirmDelete = async () => {    try {
      if (deleteTarget.kind === "node") await api.delete(`/kg/nodes/${deleteTarget.id}`);
      else await api.delete(`/kg/edges/${deleteTarget.id}`);
      toast.success("Deleted");
      setDeleteTarget(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); setDeleteTarget(null); }
  };

  if (!user) return null;
  if (user.role !== "curator" && user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center relative z-10">
        <Lock className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
        <h1 className="font-serif text-3xl text-foreground">Curator access only</h1>
        <p className="text-muted-foreground mt-2">Your current role is <b>{user.role}</b>.</p>
      </div>
    );
  }
  if (!schema) return <div className="max-w-7xl mx-auto px-6 py-16 text-muted-foreground relative z-10">Loading schema…</div>;

  const nodeTypeEntries = Object.entries(schema.node_types).map(([key, cfg]) => ({ key, ...cfg }));
  const activeNodeType = schema.node_types[active] ? { key: active, ...schema.node_types[active] } : null;
  const isAdmin = user.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 relative z-10">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-primary text-sm font-semibold uppercase tracking-wide">
            <Network className="w-4 h-4" /> Knowledge Graph
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight-serif text-foreground mt-1">Curate the Knowledge Graph</h1>
          <p className="text-muted-foreground mt-1">Edit every entity and relationship, and import seed data by CSV.</p>
        </div>
        <Button onClick={() => setResearchOpen(true)} data-testid="curator-research-btn" className="bg-[#ffb020] hover:bg-[#e69a10] text-[#14140f] shrink-0">
          <Sparkles className="w-4 h-4 mr-1.5" /> Research with AI
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
        <CuratorSidebar
          nodeTypeEntries={nodeTypeEntries}
          active={active}
          setActive={setActive}
          stats={stats}
          reviewCount={reviewCount + merges.length}
          contributionCount={contributionCount}
          dupCount={duplicates.length}
        />

        <main className="lg:col-span-9">
          {activeNodeType && (
            <NodeEditor
              nodeType={activeNodeType}
              nodes={nodes}
              nodeTotal={nodeTotal}
              pageSize={NODE_PAGE}
              nodeQuery={nodeQuery}
              nodeSkip={nodeSkip}
              onQueryChange={(v) => { setNodeSkip(0); setNodeQuery(v); }}
              onPrevPage={() => setNodeSkip(Math.max(0, nodeSkip - NODE_PAGE))}
              onNextPage={() => setNodeSkip(nodeSkip + NODE_PAGE)}
              selected={selected}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onClearSelection={() => setSelected(new Set())}
              onRunBulk={runBulk}
              onBulkDelete={() => setBulkConfirm(true)}
              onNewNode={() => { setEditingNode(null); setNodeDialog(true); }}
              onEditNode={(n) => { setEditingNode(n); setNodeDialog(true); }}
              onManageFields={() => setFieldsDialog(true)}
              onNodeAction={nodeAction}
              onDeleteNode={(id) => setDeleteTarget({ kind: "node", id })}
              isAdmin={isAdmin}
            />
          )}

          {active === "relationships" && (
            <RelationshipsPanel
              edges={edges}
              onNewEdge={() => { setEditingEdge(null); setEdgeDialog(true); }}
              onEditEdge={(e) => { setEditingEdge(e); setEdgeDialog(true); }}
              onDeleteEdge={(id) => setDeleteTarget({ kind: "edge", id })}
            />
          )}

          {active === "import" && <ImportPanel nodeTypeEntries={nodeTypeEntries} />}

          {active === "duplicates" && (
            <DuplicatesPanel groups={duplicates} nodeTypeEntries={nodeTypeEntries} onMerge={openMerge} />
          )}

          {active === "review" && <ReviewPanel reviewItems={reviewItems} merges={merges} user={user} onAction={nodeAction} onResolveMerge={resolveMerge} />}

          {active === "contributions" && <ContributionsPanel items={contributions} onResolve={resolveContribution} onCreateEntity={createEntityFromProposal} />}
        </main>
      </div>

      {activeNodeType && (
        <NodeDialog open={nodeDialog} onOpenChange={setNodeDialog} nodeType={activeNodeType} editing={editingNode} onSaved={refresh} />
      )}
      {activeNodeType && (
        <FieldsDialog open={fieldsDialog} onOpenChange={setFieldsDialog} nodeType={activeNodeType}
          isAdmin={isAdmin} onChanged={loadSchema} />
      )}
      <EdgeDialog open={edgeDialog} onOpenChange={setEdgeDialog} edgeTypes={schema.edge_types} editing={editingEdge} onSaved={refresh} />

      {proposalDialog && (
        <NodeDialog
          open={!!proposalDialog}
          onOpenChange={(o) => !o && setProposalDialog(null)}
          nodeType={proposalDialog.nodeType}
          editing={null}
          prefill={proposalDialog.prefill}
          onSaved={async () => {
            if (proposalDialog.contributionId) {
              try {
                await api.post(`/curator/contributions/${proposalDialog.contributionId}/resolve`, { action: "accept" });
                toast.success("Learner proposal accepted");
              } catch (e) { console.warn("Non-blocking: could not auto-resolve contribution", e); }
            }
            setProposalDialog(null);
            loadStats();
            loadContributions();
          }}
        />
      )}

      <ResearchPanel
        open={researchOpen}
        onOpenChange={setResearchOpen}
        contextLabel="the knowledge graph"
        onCreateEntity={createEntityFromResearch}
        enableExtract
        enableSaveNote
        onExtracted={() => { refresh(); loadStats(); }}
      />

      {mergeState && (
        <MergeDialog
          open={!!mergeState}
          onOpenChange={(o) => !o && setMergeState(null)}
          type={mergeState.type}
          typeLabel={schema.node_types[mergeState.type]?.label}
          fields={schema.node_types[mergeState.type]?.fields || []}
          members={mergeState.members}
          onProposed={() => { setMergeState(null); loadDuplicates(); loadReviewCount(); }}
        />
      )}

      <AlertDialog open={bulkConfirm} onOpenChange={(o) => !o && setBulkConfirm(false)}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} {selected.size === 1 ? "entity" : "entities"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This also removes every relationship connected to them. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => runBulk("delete")} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-bulk-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {deleteTarget?.kind}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "node"
                ? "This also removes every relationship connected to it. This cannot be undone."
                : "This removes the relationship. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KGCuratorView;
